import { Delete } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import React from 'react'
import { useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions";

/**
 * @param {Object} props 
 * @param {import("react-hook-form").UseFormReturn} props.formProps
 * @param {string} props.name
 * @param {boolean} props.disabled
 * @param {boolean} props.readOnly
 */
const OnlineMerchantTransactionAmountLimit = ({
  formProps,
  name = "currencyTxnLimitList",
  disabled,
  readOnly,
}) => {
  const CURRENCY_LIST = transactionCurrencyOptions("all");

  const { t: tLocal } = useTranslation("translation", { keyPrefix: "component.form.transactionCurrencyLimit" });
  const { control } = formProps;
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const currentCurrencyList = useWatch({ control: control, name }); // useWatch is important, allows immediate re-render on change for currencies options

  const usedCurrencies = currentCurrencyList?.map((item) => item.currency) || [];
  const usedCurrenciesOptions = usedCurrencies?.map((item) => CURRENCY_LIST.find((currency) => currency.value === item)) || [];
  const unusedCurrenciesOptions = CURRENCY_LIST.filter((currency) => !usedCurrencies?.includes(currency.value));
  const getAvailableCurrencies = (index) => {
    return [
      usedCurrenciesOptions[index],
      ...unusedCurrenciesOptions,
    ].filter(Boolean);
  }

  return (
    <FormBuilder.Section
      title={tLocal("title")}
      titleVariant="h4"
      padding="0px 16px 0px"
      extraConfigs={[
        !readOnly && {
          componentType: "button",
          config: {
            title: tLocal("addButton"),
            variant: "outline-normal-button",
            onClick: () => append({ currency: unusedCurrenciesOptions[0].value, amountLimit: "0" }),
            disabled: disabled || unusedCurrenciesOptions.length === 0,
          },
        },
      ]}>
      {
        !fields.length ?
          <Typography variant="content">{tLocal("empty")}</Typography>
          :
          fields.map((field, index) => {
            /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
            const fieldsConfig = {
              currency: {
                fieldType: "selection",
                fieldName: `${name}.${index}.currency`,
                label: tLocal("columns.currency"),
                mandatory: true,
                disabled: disabled,
                readOnly: readOnly,
                config: {
                  options: getAvailableCurrencies(index),
                },
              },
              amountLimit: {
                fieldType: "textInput",
                fieldName: `${name}.${index}.amountLimit`,
                label: tLocal("columns.amountLimit"),
                mandatory: true,
                disabled: disabled,
                readOnly: readOnly,
                config: {
                  defaultValue: 0,
                  asNumber: true,
                  rules: ["onlyPositiveNumber"],
                },
              },
            }

            return (
              <Stack
                key={field.currency}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                {FormBuilder.buildFields({
                  formProps,
                  fields: [fieldsConfig.currency, fieldsConfig.amountLimit],
                })}
                {!readOnly && <IconButton
                  size="small"
                  onClick={() => remove(index)}
                  sx={{
                    color: "error.main",
                    height: 40,
                    width: 40, // Make it square
                    borderRadius: "50%", // Make it circular
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)", // Light gray hover effect
                      borderRadius: "50%", // Maintain circular shape on hover
                    },
                  }}
                  disabled={disabled}
                >
                  <Delete fontSize="small" />
                </IconButton>}
              </Stack>
            )
          })}
    </FormBuilder.Section>

  );
}

export default OnlineMerchantTransactionAmountLimit